import { motion } from "framer-motion";
import { useLocation, Link } from "react-router-dom";
import { getLocaleFromPathname, type Locale, defaultLocale } from "@/lib/i18n";
import { getPageTranslation } from "@/lib/translations";
import { getLocalizedRoute } from "@/lib/routes";

interface CertificationsPartnershipsSectionProps {
  locale?: Locale;
}

export const CertificationsPartnershipsSection = ({
  locale: propLocale,
}: CertificationsPartnershipsSectionProps) => {
  const location = useLocation();
  const locale = propLocale || getLocaleFromPathname(location.pathname);
  const pt = getPageTranslation(locale);

  const partners = [
    {
      name: "Siemens Solution Partner",
      caption: "Solution Partner · Automation & Drives",
      logo: "/lovable-uploads/siemens-solution-partner-new.png",
      url: "https://www.partnerfinder.automation.siemens.de/s/partner-details-page?language=de&acctId=001Mz00000bbUEjIAM&c__partnerType=Solution%2520Partner",
    },
    {
      name: "Beckhoff Solution Provider",
      caption: "Solution Provider",
      logo: "/lovable-uploads/beckhoff-solution-provider.png",
      url: "https://www.beckhoff.com/de-ch/support/solution-provider-programm/w-althaus-ag.html",
    },
    {
      name: "EPLAN",
      caption: "Engineering-Software-Partner",
      logo: "/lovable-uploads/eplan-logo.png",
      url: "https://www.eplan.de",
    },
    {
      name: "Rittal",
      caption: "Application Center Partner",
      logo: "/lovable-uploads/rittal-logo.png",
      url: "https://www.rittal.com/com-en/Company/Partner-Network?country=Swiss",
    },
    {
      name: "SIGMATEK",
      caption: "Automatisierungstechnik-Partner",
      logo: "/lovable-uploads/sigmatek-logo.png",
      url: "https://www.sigmatek-automation.com",
      colored: true,
    },
  ];

  const certifications = [
    { name: "ISO 9001", caption: pt.certificationsPartnerships.qualityManagement, logo: "/lovable-uploads/iso-9001-badge.png", url: "/documents/SQS_Zertifikat_ISO_9001_14001_2015_DE.pdf" },
    { name: "ISO 14001", caption: pt.certificationsPartnerships.environmentalManagement, logo: "/lovable-uploads/iso-14001-badge.png", url: "/documents/SQS_Zertifikat_ISO_9001_14001_2015_DE.pdf" },
    { name: "EcoVadis", caption: pt.certificationsPartnerships.sustainabilityRating, logo: "/lovable-uploads/ecovadis-badge.png", url: "/documents/Ecovadis_W._Althaus_AG.pdf" },
    { name: "Lehrbetrieb", caption: pt.certificationsPartnerships.recognizedTrainingCompany, logo: "/lovable-uploads/lehrbetrieb-badge.png", url: getLocalizedRoute("vocationalTraining", locale) },
  ];

  const renderCard = (item: { name: string; caption?: string; logo: string; url?: string; colored?: boolean }, isCert = false) => {
    const inner = (
      <div className="group relative h-full rounded-2xl border border-border/60 bg-card/50 backdrop-blur-sm px-4 py-6 md:px-6 md:py-8 transition-all duration-300 hover:border-primary/40 hover:shadow-[0_10px_30px_-12px_hsl(var(--primary)/0.25)] hover:-translate-y-1 flex flex-col items-center justify-between gap-3 md:gap-4">
        <div className="flex-1 flex items-center justify-center w-full">
          <img
            src={item.logo}
            alt={item.name}
            className={`${isCert ? "h-16 md:h-20" : "h-12 md:h-14"} w-auto object-contain transition-all duration-500 group-hover:scale-105 ${item.colored ? "" : "grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100"}`}
          />
        </div>
        {item.caption && (
          <p className="text-[11px] md:text-xs text-muted-foreground text-center leading-tight tracking-wide uppercase">
            {item.caption}
          </p>
        )}
      </div>
    );

    const isInternal = item.url && !item.url.startsWith("http") && !item.url.startsWith("/documents");
    if (!item.url) return inner;
    return isInternal ? (
      <Link to={item.url} className="block h-full">{inner}</Link>
    ) : (
      <a href={item.url} target="_blank" rel="noopener noreferrer" className="block h-full">{inner}</a>
    );
  };

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-white dark:bg-background">
      <div className="container mx-auto px-6 md:px-8 max-w-6xl">

        {/* Überschrift */}
        <motion.h2
          className="text-2xl md:text-[45px] font-normal tracking-wide leading-relaxed text-center text-foreground mb-12 md:mb-20 lg:whitespace-nowrap"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          {pt.certificationsPartnerships.title}
        </motion.h2>

        {/* Partner */}
        <motion.div
          className="mb-12 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-4 mb-6 md:mb-8">
            <span className="text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-muted-foreground">
              {pt.certificationsPartnerships.partnershipsLabel}
            </span>
            <span className="flex-1 h-px bg-border/60" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {partners.map((p) => (
              <div key={p.name}>{renderCard(p, false)}</div>
            ))}
          </div>
        </motion.div>

        {/* Zertifizierungen */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-4 mb-6 md:mb-8">
            <span className="text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-muted-foreground">
              {pt.certificationsPartnerships.certificationsLabel}
            </span>
            <span className="flex-1 h-px bg-border/60" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {certifications.map((c) => (
              <div key={c.name}>{renderCard(c, true)}</div>
            ))}
          </div>
        </motion.div>

      </div>
    </section>
  );
};
